import { Component, OnInit } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { CustomComboboxComponent } from '../../shared/components/custom-combobox/custom-combobox.component';
import { CategoryService } from '../../services/categories/categories.service';
import { PortionsService } from '../../services/portions/portions.service';
import { CategoryDialogComponent } from './category-dialog.component';
import { PortionDialogComponent } from './portion-dialog.component';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';

// Interfaces locales para uso en el componente
interface Category {
  uuid: string;
  name: string;
  unitTypes?: any[];
  portions?: any[];
  enabled?: boolean;
}

interface UnitType {
  uuid: string;
  name: string;
}

@Component({
  selector: 'app-categories-admin-page',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatButtonToggleModule,
    CustomComboboxComponent,
    MatSlideToggleModule,
    MatProgressSpinnerModule,
    MatSnackBarModule
  ],
  template: `
    <div class="page">
      <div class="header">
        <h2 class="title">Categorías</h2>
      </div>

      <div class="panel">
        <div class="panel-actions top">
          <button mat-stroked-button color="primary" (click)="onNewCategory()">
            <mat-icon>add</mat-icon>
            Nueva categoría
          </button>
        </div>

        <div class="combobox-row">
          <app-custom-combobox
            class="flex-1"
            [items]="categories"
            [selectedItem]="selected"
            displayField="name"
            label="Seleccionar categoría"
            placeholder="Buscar categoría..."
            [editable]="false"
            (selectionChange)="onCategorySelect($event)">
          </app-custom-combobox>
        </div>
        <!-- Actions under combobox: visibility toggle + edit/delete -->
        <div *ngIf="selected" class="actions-under">
          <span class="toggle-wrapper">
            <mat-slide-toggle color="primary"
                               [checked]="!!selected.enabled"
                               [disabled]="categoryUpdating"
                               (change)="onToggleCategoryEnabled($event.checked)">
            </mat-slide-toggle>
            <span class="overlay" *ngIf="categoryUpdating">
              <mat-progress-spinner mode="indeterminate" diameter="16" strokeWidth="3" color="primary"></mat-progress-spinner>
            </span>
          </span>
          <button mat-icon-button color="primary" (click)="onRenameCategory()" aria-label="Renombrar categoría" [disabled]="categoryUpdating">
            <mat-icon>edit</mat-icon>
          </button>
          <button mat-icon-button color="warn" (click)="onDeleteCategory()" aria-label="Eliminar categoría" [disabled]="categoryUpdating">
            <mat-icon>delete</mat-icon>
          </button>
        </div>
        
        <!-- Portions selector for selected category -->
        <div *ngIf="selected" class="portions-block">
          <h3 class="subtitle">Porciones disponibles para: {{ selected.name }}</h3>
          <div class="portion-list">
            <label *ngFor="let p of globalPortions" class="portion-item">
              <input type="checkbox"
                     [checked]="selectedPortionUuids.includes(p.uuid)"
                     (change)="togglePortion(p.uuid, $any($event.target).checked)" />
              <span>{{ p.name }}</span>
            </label>
          </div>
          <div class="portion-actions">
            <button mat-stroked-button color="primary" (click)="onNewPortion()">
              <mat-icon>add</mat-icon>
              Nueva porción
            </button>
          </div>
          <div class="save-row">
            <button mat-stroked-button (click)="onCancel()" aria-label="Volver">
              <mat-icon>arrow_back</mat-icon>
              Volver
            </button>
            <button mat-raised-button color="primary"
                    (click)="savePortionsForSelectedCategory()"
                    [disabled]="savingPortions || !hasPortionChanges()">
              <mat-icon>save</mat-icon>
              Guardar cambios
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .page { display: flex; flex-direction: column; gap: 16px; padding: 16px; max-width: 600px; margin: 0 auto; }
    .header { display: flex; justify-content: space-between; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 8px; }
    .title { margin: 0; font-size: 1.5rem; font-weight: 500; color: #1a1a1a; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .panel { border: 1px solid #e0e0e0; border-radius: 8px; padding: 24px; box-sizing: border-box; background: white; box-shadow: 0 2px 4px rgba(0,0,0,0.05); width: 100%; max-width: 500px; margin: 0 auto; }
    .panel app-custom-combobox { display: block; width: 100%; }
    /* Collapse Material subscript (hint/error) area to remove extra gap under the combobox */
    :host ::ng-deep .panel app-custom-combobox .mat-mdc-form-field-subscript-wrapper { height: 0 !important; display: none !important; padding: 0 !important; margin: 0 !important; }
    :host ::ng-deep .panel app-custom-combobox .mat-mdc-form-field-bottom-align { height: 0 !important; }
    .combobox-row { display: flex; align-items: flex-end; gap: 8px; }
    .combobox-row .flex-1 { flex: 1 1 auto; }
    .combo-actions { display: flex; gap: 4px; }
    .panel-actions { margin: 12px 0 12px 0; display: flex; gap: 8px; justify-content: flex-start; }
    .portions-block { margin-top: 16px; }
    .subtitle { font-size: 14px; font-weight: 600; color: #374151; margin: 0 0 8px; }
    .portion-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 8px 12px; }
    .portion-item { display: flex; align-items: center; gap: 8px; padding: 6px 8px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; }
    .portion-actions { margin: 8px 0; }
    .save-row { margin-top: 12px; display: flex; gap: 8px; align-items: center; }
    .w-100 { width: 100%; }
    .actions-under { display: flex; align-items: center; gap: 8px; margin-top: 4px; }
    .toggle-wrapper { position: relative; display: inline-flex; align-items: center; justify-content: center; }
    .toggle-wrapper .overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; pointer-events: none; z-index: 2; }
  `]
})
export class CategoriesAdminPageComponent implements OnInit {
  selected: Category | null = null;
  categories: Category[] = [];
  globalUnitTypes: UnitType[] = [];
  globalPortions: { uuid: string; name: string }[] = [];
  // UI state for portions selection per category
  selectedPortionUuids: string[] = [];
  originalPortionUuids: string[] = [];
  savingPortions = false;
  categoryUpdating = false;
  
  constructor(
    private categoriesSvc: CategoryService,
    private portionsSvc: PortionsService,
    private dialog: MatDialog,
    private snack: MatSnackBar,
    private location: Location,
  ) {}

  ngOnInit(): void {
    this.loadCategories();
  }

  onCancel(): void {
    this.location.back();
  }

  async onToggleCategoryEnabled(checked: boolean) {
    if (!this.selected) return;
    const prev = !!this.selected.enabled;
    this.selected.enabled = checked;
    this.categoryUpdating = true;
    try {
      await this.categoriesSvc.updateCategory(this.selected.uuid, { enabled: checked }).toPromise();
    } catch (e) {
      console.error('Update category enabled failed', e);
      // revert on error
      this.selected.enabled = prev;
    } finally {
      this.categoryUpdating = false;
    }
  }

  async loadCategories() {
    try {
      const list = await this.categoriesSvc.getCategories().toPromise();
      this.categories = Array.isArray(list) ? list : [];
      // Do not auto-select a category; wait for user selection
      this.computeGlobalUnitTypes();
      await this.loadGlobalPortions();
    } catch (e) {
      console.error('Error loading categories', e);
      this.categories = [];
      this.selected = null;
      this.globalUnitTypes = [];
      this.globalPortions = [];
    }
  }

  computeGlobalUnitTypes() {
    const map = new Map<string, UnitType>();
    for (const c of this.categories) {
      for (const u of (c.unitTypes || [])) {
        if (u?.uuid && !map.has(u.uuid)) map.set(u.uuid, u);
      }
    }
    this.globalUnitTypes = Array.from(map.values());
  }

  async loadGlobalPortions() {
    try {
      const portions = await this.portionsSvc.getPortions().toPromise();
      this.globalPortions = (portions || []).map((p: any) => ({ uuid: String(p?.uuid || p?.id), name: String(p?.name || '') }));
    } catch (e) {
      console.error('Error loading portions', e);
      this.globalPortions = [];
    }
  }

  selectCategory(category: Category) {
    this.selected = category;
    // Initialize portions selection for the chosen category
    const portions = (category as any).portions || [];
    this.selectedPortionUuids = portions.map((p: any) => p.uuid).filter(Boolean);
    this.originalPortionUuids = [...this.selectedPortionUuids];
  }

  onCategorySelect(category: Category | null | undefined): void {
    if (!category) {
      // Clear selection and UI when combobox is cleared
      this.selected = null;
      this.selectedPortionUuids = [];
      this.originalPortionUuids = [];
      return;
    }
    this.selectCategory(category);
  }

  async onNewCategory() {
    // Cargar porciones del negocio para permitir asociarlas al crear
    let portionOptions: { uuid: string; name: string }[] = [];
    try {
      const allPortions = await this.portionsSvc.getPortions().toPromise();
      portionOptions = (allPortions || []).map((p: any) => ({ uuid: String(p?.uuid || p?.id), name: String(p?.name || '') }));
    } catch (e) {
      console.error('Load portions failed for new category', e);
    }

    const ref = this.dialog.open(CategoryDialogComponent, { data: { portionOptions, title: 'Nueva categoría', actionLabel: 'Guardar' } });
    const payload = await ref.afterClosed().toPromise();
    if (!payload || !payload.name) return;
    try {
      const created = await this.categoriesSvc.createCategory({ name: payload.name, slug: payload.slug, enabled: payload.enabled }).toPromise();
      const createdUuid = String((created as any)?.uuid || '');
      const portionUuids: string[] = Array.isArray(payload.portionUuids) ? payload.portionUuids : [];
      if (createdUuid && portionUuids.length) {
        const linkPromises = portionUuids.map(portionUuid =>
          this.categoriesSvc.linkPortionToCategories({ portionUuid, categoryUuids: [createdUuid] }).toPromise()
        );
        await Promise.all(linkPromises);
      }
      await this.loadCategories();
      const createdCat = createdUuid ? this.categories.find(c => c.uuid === createdUuid) : undefined;
      if (createdCat) this.selectCategory(createdCat as any);
    } catch (e) {
      console.error('Create category failed', e);
    }
  }

  async onNewPortion() {
    const ref = this.dialog.open(PortionDialogComponent, {
      data: { categories: this.categories.map(c => ({ uuid: c.uuid, name: c.name })) }
    });
    const payload = await ref.afterClosed().toPromise();
    if (!payload) return;
    try {
      await this.portionsSvc.createPortion(payload).toPromise();
      await this.loadCategories();
    } catch (e) {
      console.error('Create portion failed', e);
    }
  }

  async onRenameCategory() {
    if (!this.selected || this.categoryUpdating) return;

    const categoryUuid = String(this.selected.uuid || '');
    const currentPortions: any[] = (this.selected as any).portions || [];
    const currentPortionUuids: string[] = (currentPortions || []).map((p: any) => String(p?.uuid || p?.id || '')).filter(Boolean);

    try {
      const allPortions = (await this.portionsSvc.getPortions().toPromise()) as any[] || [];
      const portionOptions = (allPortions || []).map((p: any) => ({ uuid: String(p?.uuid || p?.id), name: String(p?.name || '') }));

      const ref = this.dialog.open(CategoryDialogComponent, {
        data: {
          title: 'Renombrar categoría',
          actionLabel: 'Guardar',
          name: this.selected.name,
          slug: undefined,
          categoryUuid,
          portionOptions,
          selectedPortionUuids: currentPortionUuids,
        }
      });

      const payload = await ref.afterClosed().toPromise();
      if (!payload || !payload.name) return;

      this.categoryUpdating = true;
      try {
        await this.categoriesSvc.updateCategory(this.selected.uuid, { name: payload.name }).toPromise();

        const updatedUuids: string[] = Array.isArray(payload.portionUuids) ? payload.portionUuids : currentPortionUuids;
        const prevSet = new Set(currentPortionUuids);
        const nextSet = new Set(updatedUuids);

        const toLink: string[] = [];
        const toUnlink: string[] = [];

        nextSet.forEach(u => { if (!prevSet.has(u)) toLink.push(u); });
        prevSet.forEach(u => { if (!nextSet.has(u)) toUnlink.push(u); });

        const linkPromises = toLink.map(portionUuid => {
          if (!portionUuid || !categoryUuid) return Promise.resolve();
          return this.categoriesSvc.linkPortionToCategories({ portionUuid, categoryUuids: [categoryUuid] }).toPromise();
        });

        const unlinkPromises = toUnlink.map(portionUuid => {
          if (!portionUuid || !categoryUuid) return Promise.resolve();
          return this.categoriesSvc.unlinkPortionFromCategory({ portionUuid, categoryUuid }).toPromise();
        });

        await Promise.all([...linkPromises, ...unlinkPromises]);
        await this.loadCategories();
        const updated = this.categories.find(c => c.uuid === this.selected!.uuid);
        if (updated) this.selectCategory(updated as any);
      } catch (e) {
        console.error('Rename category failed', e);
      } finally {
        this.categoryUpdating = false;
      }
    } catch (e) {
      console.error('Load portions failed for rename', e);
    }
  }

  async onDeleteCategory() {
    if (!this.selected) return;
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Eliminar categoría',
        message: `¿Eliminar la categoría "${this.selected.name}"? Esta acción no se puede deshacer.`,
        cancelLabel: 'Cancelar',
        confirmLabel: 'Eliminar',
        confirmColor: 'warn'
      }
    });
    const ok = await ref.afterClosed().toPromise();
    if (!ok) return;
    try {
      // Always delete by UUID as required
      await this.categoriesSvc.deleteCategory(this.selected.uuid).toPromise();
      await this.loadCategories();
      this.selected = null;
      this.selectedPortionUuids = [];
      this.originalPortionUuids = [];
    } catch (e: any) {
      console.error('Delete category failed', e);
      if (e?.status === 400) {
        this.snack.open('La categoría no puede ser eliminada porque tiene productos asociados.', 'Cerrar', { duration: 4000 });
      } else {
        this.snack.open('No se pudo eliminar la categoría. Inténtalo nuevamente.', 'Cerrar', { duration: 4000 });
      }
    }
  }

  async onLinkPortion() {
    if (!this.selected) return;
    // Simple chooser: pick by prompt among available portions
    const options = this.globalPortions;
    if (!options.length) return;
    const label = options.map((o, i) => `${i + 1}. ${o.name}`).join('\n');
    const idxStr = window.prompt(`Selecciona porción a vincular:\n${label}\nIngresa número (1-${options.length})`);
    const idx = Number(idxStr) - 1;
    if (isNaN(idx) || idx < 0 || idx >= options.length) return;
    const portionUuid = options[idx].uuid;
    try {
      await this.categoriesSvc.linkPortionToCategories({ portionUuid, categoryUuids: [this.selected.uuid] }).toPromise();
      await this.loadCategories();
    } catch (e) {
      console.error('Link portion failed', e);
    }
  }

  // Toggle a portion in the UI list
  togglePortion(uuid: string, checked: boolean) {
    if (checked) {
      if (!this.selectedPortionUuids.includes(uuid)) this.selectedPortionUuids.push(uuid);
    } else {
      this.selectedPortionUuids = this.selectedPortionUuids.filter(id => id !== uuid);
    }
  }

  // Compare current selection vs original to enable/disable save
  hasPortionChanges(): boolean {
    const a = new Set(this.selectedPortionUuids);
    const b = new Set(this.originalPortionUuids);
    if (a.size !== b.size) return true;
    for (const v of a) { if (!b.has(v)) return true; }
    return false;
  }

  // Persist newly added portions for the selected category
  async savePortionsForSelectedCategory() {
    if (!this.selected) return;
    const catUuid = this.selected.uuid;
    const originalSet = new Set(this.originalPortionUuids);
    const currentSet = new Set(this.selectedPortionUuids);
    const toAdd = this.selectedPortionUuids.filter(id => !originalSet.has(id));
    const toRemove = this.originalPortionUuids.filter(id => !currentSet.has(id));
    if (!toAdd.length && !toRemove.length) return;
    this.savingPortions = true;
    try {
      // Link newly selected portions
      const linkPromises = toAdd.map(portionUuid =>
        this.categoriesSvc.linkPortionToCategories({ portionUuid, categoryUuids: [catUuid] }).toPromise()
      );
      // Unlink deselected portions
      const unlinkPromises = toRemove.map(portionUuid =>
        this.categoriesSvc.unlinkPortionFromCategory({ portionUuid, categoryUuid: catUuid }).toPromise()
      );
      await Promise.all([...linkPromises, ...unlinkPromises]);
      await this.loadCategories();
      // Re-select the current category to refresh UI state
      const updated = this.categories.find(c => c.uuid === catUuid);
      if (updated) this.selectCategory(updated as any);
    } catch (e) {
      console.error('Failed to link portions', e);
    } finally {
      this.savingPortions = false;
    }
  }
}
